import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type {
  AutoPrintCatalog,
  AutoPrintCopiesMode,
  AutoPrintRule,
  CreateAutoPrintRuleRequest,
  PrinterAdminRow,
  UpdateAutoPrintRuleRequest,
} from '@techgitter/contracts';

interface AutoPrintRuleEditDialogProps {
  open: boolean;
  /** The row being edited, or null when creating. */
  rule: AutoPrintRule | null;
  /** Code catalog driving the trigger / document / copies-mode pickers. */
  catalog: AutoPrintCatalog | null;
  /** Printer fleet driving the (siteId, role) picker. */
  printers: PrinterAdminRow[];
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (
    payload: CreateAutoPrintRuleRequest | UpdateAutoPrintRuleRequest,
    id: string | null,
  ) => void;
}

const ACCENT = '#4A90D9';

const COPIES_MODE_LABELS: Record<AutoPrintCopiesMode, string> = {
  per_unit: 'Per unit',
  fixed: 'Fixed',
};

/** Composite key for the printer <select> — (siteId, role) is the fleet identity. */
const printerKey = (siteId: string, role: string): string => `${siteId}${role}`;

export const AutoPrintRuleEditDialog: React.FC<AutoPrintRuleEditDialogProps> = ({
  open,
  rule,
  catalog,
  printers,
  isSaving,
  error,
  onClose,
  onSave,
}) => {
  const isCreate = rule === null;

  const [trigger, setTrigger] = useState('');
  const [document, setDocument] = useState('');
  const [siteId, setSiteId] = useState('');
  const [role, setRole] = useState('');
  const [copiesMode, setCopiesMode] = useState<AutoPrintCopiesMode>('per_unit');
  const [copies, setCopies] = useState('1');
  const [isActive, setIsActive] = useState(true);

  // Reset/seed the form whenever the dialog (re)opens.
  useEffect(() => {
    if (!open) return;
    setTrigger(rule?.trigger ?? '');
    setDocument(rule?.document ?? '');
    setSiteId(rule?.siteId ?? '');
    setRole(rule?.role ?? '');
    setCopiesMode(rule?.copiesMode ?? 'per_unit');
    setCopies(rule?.copies != null ? String(rule.copies) : '1');
    setIsActive(rule?.isActive ?? true);
  }, [open, rule]);

  const maxCopies = catalog?.maxCopies ?? 10;
  const triggers = catalog?.triggers ?? [];
  const documents = catalog?.documents ?? [];
  const copiesModes = catalog?.copiesModes ?? ['per_unit', 'fixed'];

  const selectedTrigger = triggers.find((t) => t.key === trigger);
  const selectedDocument = documents.find((d) => d.key === document);

  // Printer options from the fleet; keep an out-of-fleet target selectable when
  // editing a rule whose printer was since deleted/renamed.
  const printerOptions = useMemo(() => {
    const opts = printers.map((p) => ({
      key: printerKey(p.siteId, p.role),
      siteId: p.siteId,
      role: p.role,
      label: `${p.siteId} · ${p.role}`,
    }));
    if (siteId && role && !printers.some((p) => p.siteId === siteId && p.role === role)) {
      opts.push({
        key: printerKey(siteId, role),
        siteId,
        role,
        label: `${siteId} · ${role} (not in fleet)`,
      });
    }
    return opts.sort(
      (a, b) => a.siteId.localeCompare(b.siteId) || a.role.localeCompare(b.role),
    );
  }, [printers, siteId, role]);

  const printerValue = siteId && role ? printerKey(siteId, role) : '';

  const handlePrinterChange = (value: string) => {
    const opt = printerOptions.find((o) => o.key === value);
    setSiteId(opt?.siteId ?? '');
    setRole(opt?.role ?? '');
  };

  const copiesNum = Number(copies);
  const copiesValid =
    copiesMode !== 'fixed' ||
    (Number.isInteger(copiesNum) && copiesNum >= 1 && copiesNum <= maxCopies);

  const isValid =
    trigger !== '' &&
    document !== '' &&
    siteId !== '' &&
    role !== '' &&
    copiesValid;

  const handleSave = () => {
    const effectiveCopies = copiesMode === 'fixed' ? copiesNum : null;

    if (isCreate) {
      const payload: CreateAutoPrintRuleRequest = {
        trigger,
        document,
        siteId,
        role,
        copiesMode,
        copies: effectiveCopies,
        isActive,
      };
      onSave(payload, null);
      return;
    }

    // Edit: send only the fields that actually changed.
    const patch: UpdateAutoPrintRuleRequest = {};
    if (trigger !== rule!.trigger) patch.trigger = trigger;
    if (document !== rule!.document) patch.document = document;
    if (siteId !== rule!.siteId) patch.siteId = siteId;
    if (role !== rule!.role) patch.role = role;
    if (copiesMode !== rule!.copiesMode) patch.copiesMode = copiesMode;
    if (effectiveCopies !== rule!.copies) patch.copies = effectiveCopies;
    if (isActive !== rule!.isActive) patch.isActive = isActive;
    onSave(patch, rule!.id);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isCreate ? 'New AutoPrint rule' : 'Edit AutoPrint rule'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="Trigger"
            value={trigger}
            onChange={(e) => setTrigger(e.target.value)}
            required
            size="small"
            fullWidth
            helperText={
              selectedTrigger
                ? selectedTrigger.description
                : 'The event that fires the print.'
            }
          >
            {triggers.map((t) => (
              <MenuItem key={t.key} value={t.key}>
                {t.key}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Document"
            value={document}
            onChange={(e) => setDocument(e.target.value)}
            required
            size="small"
            fullWidth
            helperText={
              selectedDocument
                ? `${selectedDocument.kind} — ${selectedDocument.description}`
                : 'The document to print.'
            }
          >
            {documents.map((d) => (
              <MenuItem key={d.key} value={d.key}>
                {d.key} &middot; {d.kind}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Printer"
            value={printerValue}
            onChange={(e) => handlePrinterChange(e.target.value)}
            required
            size="small"
            fullWidth
            helperText={
              printerOptions.length === 0
                ? 'No printers configured — add one on the Printers page first.'
                : 'Target lane in the printer fleet (site · role).'
            }
          >
            {printerOptions.length === 0 ? (
              <MenuItem value="" disabled>
                <em>No printers configured</em>
              </MenuItem>
            ) : (
              printerOptions.map((o) => (
                <MenuItem key={o.key} value={o.key}>
                  {o.label}
                </MenuItem>
              ))
            )}
          </TextField>

          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Copies mode"
              value={copiesMode}
              onChange={(e) => setCopiesMode(e.target.value as AutoPrintCopiesMode)}
              size="small"
              fullWidth
              helperText={
                copiesMode === 'per_unit'
                  ? 'One copy per affected unit'
                  : 'A fixed number of copies'
              }
            >
              {copiesModes.map((m) => (
                <MenuItem key={m} value={m}>
                  {COPIES_MODE_LABELS[m] ?? m}
                </MenuItem>
              ))}
            </TextField>
            {copiesMode === 'fixed' && (
              <TextField
                label="Copies"
                value={copies}
                onChange={(e) => setCopies(e.target.value)}
                required
                size="small"
                type="number"
                sx={{ width: 160 }}
                inputProps={{ min: 1, max: maxCopies }}
                error={!copiesValid}
                helperText={`1–${maxCopies}`}
              />
            )}
          </Stack>

          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            }
            label="Active"
          />
          <Typography variant="caption" sx={{ color: '#888' }}>
            Inactive rules never fire. Deleting a rule frees its (trigger, site,
            role, document) slot.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isValid || isSaving}
          sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#3B82C4' } }}
        >
          {isSaving ? 'Saving…' : isCreate ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
